import { Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { COLOR_PALETTE, buildGradientFromColor } from '../../utils/colorUtils';

interface ColorPickerProps {
    selectedColor: string | null;
    onColorChange: (color: string | null) => void;
    defaultColor?: string;
    onReset?: () => void;
}

export default function ColorPicker({
    selectedColor,
    onColorChange,
    defaultColor,
    onReset,
}: ColorPickerProps) {
    const { t } = useTranslation();

    return (
        <div className={'flex flex-col gap-3'}>
            <div className={'flex items-center justify-between'}>
                <label className={'text-navy text-body-sm font-medium'}>
                    {t('visionValues.backgroundColorLabel')}
                </label>

                {selectedColor && (
                    <button
                        type={'button'}
                        onClick={() => {
                            onColorChange(null);
                            onReset?.();
                        }}
                        className={'text-primary text-caption hover:underline'}
                    >
                        {t('visionValues.resetToIconColor')}
                    </button>
                )}
            </div>

            <p className={'text-caption -mt-1 text-gray-500'}>
                {t('visionValues.backgroundColorHint')}
            </p>

            <div className={'flex flex-wrap gap-2 rounded-lg border border-gray-300 bg-white p-3'}>
                {COLOR_PALETTE.map((color) => (
                    <button
                        key={color}
                        type={'button'}
                        onClick={() => onColorChange(color)}
                        className={`h-10 w-10 rounded-lg transition-all ${
                            selectedColor === color
                                ? 'ring-primary scale-110 ring-2 ring-offset-2'
                                : 'hover:scale-110'
                        }`}
                        style={{ background: buildGradientFromColor(color) }}
                        title={color}
                        aria-label={color}
                    />
                ))}

                {/* Custom color picker */}
                <label
                    className={
                        'relative flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 transition-colors hover:border-teal-400'
                    }
                    title={t('visionValues.customColor')}
                >
                    <input
                        type={'color'}
                        value={selectedColor || defaultColor || '#0DD3C3'}
                        onChange={(e) => onColorChange(e.target.value)}
                        className={'absolute inset-0 cursor-pointer opacity-0'}
                    />

                    <Icon
                        name={'essential-add'}
                        size={'sm'}
                        colorClassName={'text-gray-400'}
                    />
                </label>
            </div>
        </div>
    );
}
